import clsx from 'clsx'
import {
  shouldShowAddClassColumn,
  shouldShowAddGradeColumn,
  shouldShowAddStaffColumn,
  shouldShowAddStudentColumn,
  shouldShowAddSubjectColumn,
  shouldShowAddTeacherColumn,
  shouldShowUpdateClassColumn,
  shouldShowUpdateGradeColumn,
  shouldShowUpdateStaffColumn,
  shouldShowUpdateStudentColumn,
  shouldShowUpdateSubjectColumn,
  shouldShowUpdateTeacherColumn,
} from 'common/HelperFunctions'
import {ErrorMessage, Field, useField} from 'formik'
import {FC, Fragment, useEffect, useId, useMemo, useState} from 'react'
import {OverlayTrigger, Tooltip} from 'react-bootstrap'
import {useIntl} from 'react-intl'
import {useSelector} from 'react-redux'

type Props = {
  name: string
  label?: string
  type?: string
  className?: string
  fieldClassName?: string
  labelClassName?: string
  required?: boolean
  showError?: boolean
  accept?: string
  title?: string
  placeholder?: string
  helperText?: string
  readOnly?: boolean
  applyInvalidClass?: boolean
  list?: any[]
  maxLength?: number
  step?: number
  [key: string]: any
  inputRef?: React.Ref<any>
  onChange?: (value: any) => void
  featureColumn?: string
  featureScope?:
    | 'add_class'
    | 'update_class'
    | 'add_subject'
    | 'update_subject'
    | 'add_grade'
    | 'update_grade'
    | 'add_student'
    | 'update_student'
    | 'add_teacher'
    | 'update_teacher'
    | 'add_staff'
    | 'update_staff'
}

const CustomField: FC<Props> = ({
  name,
  label = '',
  type = 'text',
  className = '',
  fieldClassName,
  labelClassName,
  required = false,
  showError = true,
  accept,
  title,
  placeholder,
  helperText,
  readOnly = false,
  applyInvalidClass = true,
  children,
  maxLength,
  onChange,
  step = 0.05,
  featureColumn,
  featureScope,
  inputRef,
  ...restProps
}) => {
  const intl = useIntl()
  const featuresString = useSelector((state: any) => state.mainReducer.generalSettings?.features)
  const isAllowedByFeatures = useMemo(() => {
    if (!featureColumn || !featureScope) return true
    if (featureScope === 'add_class') return shouldShowAddClassColumn(featureColumn, featuresString)
    if (featureScope === 'update_class')
      return shouldShowUpdateClassColumn(featureColumn, featuresString)
    if (featureScope === 'add_subject')
      return shouldShowAddSubjectColumn(featureColumn, featuresString)
    if (featureScope === 'update_subject')
      return shouldShowUpdateSubjectColumn(featureColumn, featuresString)
    if (featureScope === 'add_grade') return shouldShowAddGradeColumn(featureColumn, featuresString)
    if (featureScope === 'update_grade')
      return shouldShowUpdateGradeColumn(featureColumn, featuresString)
    if (featureScope === 'add_student')
      return shouldShowAddStudentColumn(featureColumn, featuresString)
    if (featureScope === 'update_student')
      return shouldShowUpdateStudentColumn(featureColumn, featuresString)
    if (featureScope === 'add_teacher')
      return shouldShowAddTeacherColumn(featureColumn, featuresString)
    if (featureScope === 'update_teacher')
      return shouldShowUpdateTeacherColumn(featureColumn, featuresString)
    if (featureScope === 'add_staff') return shouldShowAddStaffColumn(featureColumn, featuresString)
    if (featureScope === 'update_staff')
      return shouldShowUpdateStaffColumn(featureColumn, featuresString)
    return true
  }, [featureColumn, featureScope, featuresString])
  const [field, meta, helpers] = useField(name)
  const [textLength, setTextLength] = useState(0)
  const [showPassword, setShowPassword] = useState(false)
  const reactId = useId()
  const id = `${name}-${reactId}`

  const fieldType = useMemo(() => {
    return type === 'float'
      ? 'number'
      : type === 'password'
        ? showPassword
          ? 'text'
          : 'password'
        : type
  }, [type, showPassword])

  const labelHasTranslation = label ? !!intl.messages[label] : false
  const titleHasTranslation = title ? !!intl.messages[title] : false

  const renderTooltip = useMemo(() => {
    return (
      <Tooltip id={`tooltip-${id}`}>
        {titleHasTranslation ? intl.formatMessage({id: title}) : title}
      </Tooltip>
    )
  }, [title, id, intl, titleHasTranslation])

  useEffect(() => {
    if (typeof onChange === 'function') {
      onChange(field.value)
    }
  }, [field.value, onChange])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const value = e.target.value

    // Handle float type separately
    if (type === 'float') {
      helpers.setValue(value)
      return
    }

    if (maxLength && value.length <= maxLength) {
      helpers.setValue(value)
      setTextLength(value.length)
    } else if (!maxLength) {
      helpers.setValue(value)
      setTextLength(value.length)
    }
  }

  const FieldWrapper = type === 'password' ? 'div' : Fragment
  // Rendered directly (not via formik's <Field>) — useField above already
  // subscribes this component; a nested <Field> just adds a second context
  // subscriber that re-renders on every keystroke anywhere in the form.
  const FieldElement = (
    type === 'select' ? 'select' : type === 'textarea' ? 'textarea' : 'input'
  ) as any

  if (!isAllowedByFeatures) {
    return null
  }

  return (
    <div className={clsx('mb-3', className)}>
      {label && (
        <label className={clsx('form-label', labelClassName, {required})} htmlFor={id}>
          {labelHasTranslation ? intl.formatMessage({id: label}) : label}
          {title && (
            <OverlayTrigger placement='right' overlay={renderTooltip}>
              <div className='badge pe-auto'>
                <i className='bi bi-info-circle-fill'></i>
              </div>
            </OverlayTrigger>
          )}
        </label>
      )}

      {type !== 'file' && (
        <FieldWrapper {...(type === 'password' ? {className: 'position-relative'} : {})}>
          <FieldElement
            id={id}
            name={name}
            type={fieldType}
            placeholder={placeholder}
            step={type === 'float' ? step : undefined}
            autoComplete='new-password'
            aria-autocomplete='none'
            ref={inputRef}
            className={clsx(fieldClassName, {
              'form-control': type !== 'select',
              'form-select': type === 'select',
              'is-invalid': applyInvalidClass && meta.touched && meta.error,
              'pe-12': type === 'password',
            })}
            readOnly={readOnly}
            onChange={handleChange}
            onBlur={field.onBlur}
            value={field.value ?? ''}
            {...restProps}
          >
            {children}
          </FieldElement>

          {type === 'password' && (
            <span
              className={clsx(
                'position-absolute end-0 top-50 translate-middle-y',
                meta.touched && meta.error ? 'me-12' : 'me-5'
              )}
            >
              <OverlayTrigger
                placement='top'
                overlay={
                  <Tooltip id='tooltip-show-hide-password'>
                    {showPassword ? 'Hide' : 'Show'} Password
                  </Tooltip>
                }
              >
                <i
                  className={clsx('cursor-pointer fa', {
                    'fa-eye': !showPassword,
                    'fa-eye-slash': showPassword,
                  })}
                  onClick={() => setShowPassword(!showPassword)}
                  tabIndex={3}
                  aria-hidden='true'
                  aria-label='Show password'
                  role='button'
                />
              </OverlayTrigger>
            </span>
          )}
        </FieldWrapper>
      )}

      {type === 'file' && (
        <Field
          id={id}
          name={name}
          type='file'
          accept={accept}
          className={`form-control ${meta.touched && meta.error ? 'is-invalid' : ''}`}
          value={undefined}
          onChange={(event: any) => {
            const files = restProps?.multiple
              ? event?.currentTarget?.files
              : event?.currentTarget?.files[0]
            helpers.setValue(files)
          }}
          readOnly={readOnly}
          {...restProps}
        />
      )}

      {type === 'textarea' && maxLength && (
        <div style={{marginTop: '10px'}}>
          {textLength}/{maxLength}
        </div>
      )}

      {helperText && (
        <small id={`${id}-help-text`} className='form-text text-muted'>
          {helperText}
        </small>
      )}

      {showError && (
        <ErrorMessage
          component='div'
          name={name}
          className={clsx('mt-2', {
            'invalid-feedback': applyInvalidClass && type !== 'password',
            'text-danger': !applyInvalidClass || type === 'password',
          })}
        />
      )}
    </div>
  )
}

export default CustomField
